/** Marketing benefits for campaign host subscription UI (titles + short descriptions). */
export const CAMPAIGN_HOST_SUITE_FEATURES = [
    {
        key: 'unlimited' as const,
        title: 'Unlimited campaign creation',
        description: 'Deploy infinite campaigns without monthly caps.',
    },
    {
        key: 'saveFees' as const,
        title: 'Save up to 50% in service fees',
        description: 'Pay less on platform fees across eligible campaign spend.',
    },
    {
        key: 'zeroFees' as const,
        title: 'Zero campaign fees',
        description: 'No platform service fees on eligible campaign spend.',
    },
    {
        key: 'priority' as const,
        title: 'Priority campaign placement',
        description: 'Get boosted visibility when your campaigns go live.',
    },
    {
        key: 'hireCreators' as const,
        title: 'Hire unlimited Creators',
        description: 'Scale roster size for large programs with sales-assisted onboarding.',
    },
    {
        key: 'hireCreators50' as const,
        title: 'Hire up to 50 Creators',
        description: 'Invite up to 50 creators per campaign on the Monthly plan.',
    },
    {
        key: 'instagramAutomation' as const,
        title: 'Instagram automation',
        description:
            'Publish or schedule from your brand workspace, and auto-send product links in Instagram comments and DMs.',
    },
    {
        key: 'smsLiveUrl' as const,
        title: 'Live URL SMS alerts',
        description: 'Get a text when a creator submits the final live URL for your approval.',
    },
] as const;

export type CampaignHostTierFeatureKey = (typeof CAMPAIGN_HOST_SUITE_FEATURES)[number]['key'];

/** @deprecated Use CAMPAIGN_HOST_SUITE_FEATURES[].title */
export const CAMPAIGN_HOST_TIER_MARKETING_FEATURES = Object.fromEntries(
    CAMPAIGN_HOST_SUITE_FEATURES.map(f => [f.key, f.title]),
) as Record<CampaignHostTierFeatureKey, string>;

export interface CampaignHostTierFeatureRow {
    key: CampaignHostTierFeatureKey;
    included: boolean;
}

export type CampaignHostTierKind = 'monthly' | 'ltd' | 'enterprise';

export function getSuiteFeature(key: CampaignHostTierFeatureKey) {
    return CAMPAIGN_HOST_SUITE_FEATURES.find(f => f.key === key)!;
}

/** Monthly: unlimited creates, fee savings, up to 50 creators per campaign. */
export const MONTHLY_TIER_FEATURE_ROWS: CampaignHostTierFeatureRow[] = [
    { key: 'unlimited', included: true },
    { key: 'saveFees', included: true },
    { key: 'hireCreators50', included: true },
    { key: 'instagramAutomation', included: true },
    { key: 'smsLiveUrl', included: true },
];

/** Lifetime: core benefits plus priority placement. */
export const LTD_TIER_FEATURE_ROWS: CampaignHostTierFeatureRow[] = [
    { key: 'unlimited', included: true },
    { key: 'saveFees', included: true },
    { key: 'priority', included: true },
    { key: 'instagramAutomation', included: true },
    { key: 'smsLiveUrl', included: true },
];

/** Enterprise: unlimited creates, zero fees, large creator hire (sales-assisted). */
export const ENTERPRISE_TIER_FEATURE_ROWS: CampaignHostTierFeatureRow[] = [
    { key: 'unlimited', included: true },
    { key: 'zeroFees', included: true },
    { key: 'hireCreators', included: true },
    { key: 'instagramAutomation', included: true },
    { key: 'smsLiveUrl', included: true },
];

export function getTierFeatureRows(kindOrFeatured: CampaignHostTierKind | boolean): CampaignHostTierFeatureRow[] {
    if (kindOrFeatured === true || kindOrFeatured === 'ltd') {
        return LTD_TIER_FEATURE_ROWS;
    }
    if (kindOrFeatured === 'enterprise') {
        return ENTERPRISE_TIER_FEATURE_ROWS;
    }
    return MONTHLY_TIER_FEATURE_ROWS;
}

export function getActiveSubscriptionFeatureRows(isLifetime: boolean): CampaignHostTierFeatureRow[] {
    return isLifetime ? LTD_TIER_FEATURE_ROWS : MONTHLY_TIER_FEATURE_ROWS;
}
